import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CountrySelector from '../../../Components/CountrySelector';
import StateSelector from '../../../Components/StateSelector';
import { patchInternalDonationLineCorrections } from '../../../endpoints/donations';
import { getCountries } from '../../../endpoints/lookups';
import { useApiGet } from '../../../utils/httpClient';
import { getApiErrorMessage } from '../../../utils/httpClientTs';
import { formatCurrency } from '../../../utils/formatters';

function mapNotificationFromLine(n, countries) {
  const country = countries?.find((c) => c.id === n.country_id);
  return {
    id: n.id,
    notification_first_name: n.first_name || '',
    notification_last_name: n.last_name || '',
    email: n.email || '',
    country_id: n.country_id || '',
    state_id: n.state_id || '',
    state_required: country?.has_states ?? !!n.state_id,
    city: n.city || '',
    postal_code: n.postal_code || '',
    address: n.address1 || '',
  };
}

function emptyLineState(line, countries) {
  return {
    behalfOfFullName: line.behalf_of_name || line.full_name || '',
    notifications: (line.notifications ?? []).map((n) =>
      mapNotificationFromLine(n, countries)
    ),
  };
}

export default function DonationLineCorrections({
  donationId,
  donationObject,
  refetch,
  handleBack,
  onSaved,
}) {
  const lines = donationObject?.donation_lines ?? [];
  const [lineState, setLineState] = useState({});
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const { data: countries } = useApiGet(['donation-countries'], getCountries, {
    enabled: true,
  });

  useEffect(() => {
    const donationLines = donationObject?.donation_lines ?? [];
    const next = {};
    donationLines.forEach((line) => {
      next[line.id] = emptyLineState(line);
    });
    setLineState(next);
  }, [donationObject]);

  useEffect(() => {
    if (!countries?.length) return;
    setLineState((prev) => {
      const donationLines = donationObject?.donation_lines ?? [];
      if (Object.keys(prev).length === 0 && donationLines.length > 0) {
        const next = {};
        donationLines.forEach((line) => {
          next[line.id] = emptyLineState(line, countries);
        });
        return next;
      }
      let anyChanged = false;
      const next = {};
      for (const [lineId, line] of Object.entries(prev)) {
        let lineChanged = false;
        const notifications = line.notifications.map((n) => {
          const country = countries.find((c) => c.id === n.country_id);
          const stateRequired = country?.has_states ?? !!n.state_id;
          if (n.state_required === stateRequired) return n;
          lineChanged = true;
          return { ...n, state_required: stateRequired };
        });
        if (lineChanged) {
          anyChanged = true;
          next[lineId] = { ...line, notifications };
        } else {
          next[lineId] = line;
        }
      }
      return anyChanged ? next : prev;
    });
  }, [countries, donationObject]);

  const updateLineField = useCallback((lineId, field, value) => {
    setLineState((prev) => ({
      ...prev,
      [lineId]: { ...prev[lineId], [field]: value },
    }));
  }, []);

  const updateNotificationField = useCallback(
    (lineId, notifId, field, value) => {
      setLineState((prev) => {
        const line = prev[lineId];
        if (!line) return prev;
        return {
          ...prev,
          [lineId]: {
            ...line,
            notifications: line.notifications.map((n) =>
              n.id === notifId ? { ...n, [field]: value } : n
            ),
          },
        };
      });
    },
    []
  );

  const handleNotificationCountryChange = useCallback(
    (lineId, notifId, value) => {
      setLineState((prev) => {
        const line = prev[lineId];
        if (!line) return prev;
        return {
          ...prev,
          [lineId]: {
            ...line,
            notifications: line.notifications.map((n) => {
              if (n.id !== notifId) return n;
              const countryChanged = value?.id !== n.country_id;
              return {
                ...n,
                country_id: value?.id || '',
                state_required: value?.has_states ?? false,
                state_id: countryChanged ? '' : n.state_id,
              };
            }),
          },
        };
      });
    },
    []
  );

  const handleNotificationStateChange = useCallback(
    (lineId, notifId, value) => {
      updateNotificationField(lineId, notifId, 'state_id', value?.id || '');
    },
    [updateNotificationField]
  );

  const handleSaveAll = async () => {
    setErrorMsg(null);
    setSuccessMsg(null);
    setSaving(true);
    try {
      for (const line of lines) {
        const state = lineState[line.id];
        if (!state) continue;
        const hasBehalfOf = !!(line.behalf_of_name || line.full_name);
        const payload = {
          notifications: state.notifications.map((n) => ({
            id: n.id,
            notification_first_name: n.notification_first_name,
            notification_last_name: n.notification_last_name,
            email: n.email,
            country_id: n.country_id,
            state_id: n.state_id,
            city: n.city,
            postal_code: n.postal_code,
            address: n.address,
          })),
        };
        if (hasBehalfOf) {
          payload.behalf_of_full_name = state.behalfOfFullName;
        }
        await patchInternalDonationLineCorrections({
          donationId,
          lineId: line.id,
          ...payload,
        });
      }
      await refetch();
      setSuccessMsg('Line copy saved.');
      onSaved?.();
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (lines.length === 0) {
    return (
      <Box>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No donation lines to edit.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button onClick={handleBack}>Back</Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Fix honoree names and notification recipient spelling or addresses.
        Amounts and projects cannot be changed here.
      </Typography>

      {errorMsg && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setErrorMsg(null)}
        >
          {errorMsg}
        </Alert>
      )}
      {successMsg && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccessMsg(null)}
        >
          {successMsg}
        </Alert>
      )}

      <Stack spacing={2}>
        {lines.map((line, index) => {
          const state = lineState[line.id] || emptyLineState(line, countries);
          const hasBehalfOf = !!(line.behalf_of_name || line.full_name);
          return (
            <Card key={line.id} variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Line {index + 1}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {formatCurrency(line.donation_amount)} · {line.project_name}
                  {line.fund_name ? ` · ${line.fund_name}` : ''}
                </Typography>

                {hasBehalfOf && (
                  <TextField
                    fullWidth
                    label="In honor/memory of (honoree name)"
                    value={state.behalfOfFullName}
                    onChange={(e) =>
                      updateLineField(
                        line.id,
                        'behalfOfFullName',
                        e.target.value
                      )
                    }
                    sx={{ mb: 2 }}
                  />
                )}

                {state.notifications.length > 0 && (
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Notifications
                  </Typography>
                )}
                {state.notifications.map((notif) => (
                  <Box
                    key={notif.id}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="First name"
                          value={notif.notification_first_name}
                          onChange={(e) =>
                            updateNotificationField(
                              line.id,
                              notif.id,
                              'notification_first_name',
                              e.target.value
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Last name"
                          value={notif.notification_last_name}
                          onChange={(e) =>
                            updateNotificationField(
                              line.id,
                              notif.id,
                              'notification_last_name',
                              e.target.value
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Email"
                          value={notif.email}
                          onChange={(e) =>
                            updateNotificationField(
                              line.id,
                              notif.id,
                              'email',
                              e.target.value
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Address"
                          value={notif.address}
                          onChange={(e) =>
                            updateNotificationField(
                              line.id,
                              notif.id,
                              'address',
                              e.target.value
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <CountrySelector
                          size="small"
                          value={notif.country_id}
                          handleChange={(value) =>
                            handleNotificationCountryChange(
                              line.id,
                              notif.id,
                              value
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Postal code"
                          value={notif.postal_code}
                          onChange={(e) =>
                            updateNotificationField(
                              line.id,
                              notif.id,
                              'postal_code',
                              e.target.value
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="City"
                          value={notif.city}
                          onChange={(e) =>
                            updateNotificationField(
                              line.id,
                              notif.id,
                              'city',
                              e.target.value
                            )
                          }
                        />
                      </Grid>
                      {notif.state_required && (
                        <Grid item xs={12} sm={6}>
                          <StateSelector
                            size="small"
                            value={notif.state_id}
                            countryId={notif.country_id}
                            handleChange={(value) =>
                              handleNotificationStateChange(
                                line.id,
                                notif.id,
                                value
                              )
                            }
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                ))}
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
        <Button onClick={handleBack} disabled={saving}>
          Back
        </Button>
        <Button variant="contained" disabled={saving} onClick={handleSaveAll}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </Stack>
    </Box>
  );
}
